<template>

  <v-container fluid :style="`height:${height}`" class="map-viewer">
    <!-- Map Viewer -->
    <div id="viewer-mapbox" class="viewer-mapbox" />

    <template v-if="map">
      <!-- Markers -->
      <template v-for="(marker, index) in markers">
        <map-marker
            :map="map"
            :key="`marker-${index}`"
            :id="`marker-${index}`"
            :coordinates="marker.coordinates"
            :properties="marker.properties" />
      </template>

      <!-- Delivery Steps -->
      <template v-for="(step, index) in deliverySteps">
        <map-marker
            :map="map"
            :key="`delivery-step-${index}`"
            :id="`delivery-step-${index}`"
            :coordinates="step.coordinates"
            :properties="step.properties" />
      </template>

      <!-- Distribution Pairs -->
      <template v-for="(pair, index) in distributions">
        <map-marker
            v-if="pair.pickup_location"
            :map="map"
            :key="`pickup-${index}`"
            :id="`pickup-${index}`"
            :coordinates="pair.pickup_location.coordinates"
            :properties="pair.pickup_location.properties" />
        <map-marker
            v-if="pair.dropoff_location"
            :map="map"
            :key="`dropoff-${index}`"
            :id="`dropoff-${index}`"
            :coordinates="pair.dropoff_location.coordinates"
            :properties="pair.dropoff_location.properties" />
      </template>
    </template>
  </v-container>

</template>

<script>
/* global mapboxgl */
import MapMarker from '@/components/map/MapMarker'

export default {
  name: 'MapViewer',

  components: {
    MapMarker
  },

  props: {
    // General map markers
    markers: {
      type: Array,
      default: () => []
    },
    // Distribution pairs (pickup,dropoff location pairs)
    distributions: {
      type: Array,
      default: () => []
    },
    // Delivery steps
    deliverySteps: {
      type: Array,
      default: () => []
    },
    // Enables route layer
    enableRouteLayer: {
      type: Boolean,
      default: true
    },
    // A route path to display (geojson format)
    routeLayer: {
      type: Object,
      default: null
    },
    // Override settings for map (optional)
    settings: {
      type: Object,
      default: () => {}
    },
    // Display height (% or px)
    height: {
      type: String,
      default: '563px'
    }
  },

  data () {
    return {
      map: null,
      showNavControl: false,
      showFullScreenControl: false,
      defaultBasemap: 'mapbox://styles/mapbox/streets-v11',
      defaultSettings: {
        minZoom: 5, // lower to allow more zoom out
        // maxZoom: 10, // raise to allow more zoom in
        center: [-112.4052378, 33.6050991], // phoenix AZ US
        zoom: 9,
        pitch: 0
      }
    }
  },

  watch: {
    // Update route layer
    routeLayer: function (updatedLayer) {
      const sourceLayer = this.map.getSource('route')
      if (updatedLayer && sourceLayer) sourceLayer.setData(updatedLayer)
    }
  },

  mounted () {
    // Prepare settings
    const settings = Object.assign({}, this.defaultSettings, this.settings)

    // Load map instance into DOM
    mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN
    this.map = new mapboxgl.Map({
      container: 'viewer-mapbox',
      style: this.defaultBasemap,
      ...settings
    })

    // Enable configured controls
    if (this.showNavControl) this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
    if (this.showFullScreenControl) this.map.addControl(new mapboxgl.FullscreenControl())

    this.map.on('load', async () => {
      // Enable route display capabilities
      if (this.enableRouteLayer) {
        this.renderRouteDisplayLayer(this.routeLayer)
      }
      // Emit loaded map
      this.$emit('loaded', this.map)
    })
  },

  methods: {
    renderRouteDisplayLayer (data) {
      this.map.addSource('route', {
        type: 'geojson',
        data
      })

      this.map.addLayer(
        {
          id: 'delivery-route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#3887be',
            'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3, 22, 12]
          }
        },
        'waterway-label'
      )

      this.map.addLayer(
        {
          id: 'delivery-direction',
          type: 'symbol',
          source: 'route',
          layout: {
            'symbol-placement': 'line',
            'text-field': '▶',
            'text-size': ['interpolate', ['linear'], ['zoom'], 12, 24, 22, 60],
            'symbol-spacing': ['interpolate', ['linear'], ['zoom'], 12, 30, 22, 160],
            'text-keep-upright': false
          },
          paint: {
            'text-color': '#3887be',
            'text-halo-color': 'hsl(55, 11%, 96%)',
            'text-halo-width': 3
          }
        },
        'waterway-label'
      )
    }
  }
}
</script>

<style>
  .map-viewer {
    border: 1px solid #525252;
    border-radius: 2px;
    width: 100%;
  }

  .viewer-mapbox {
    width: 100%;
    height: 100%;
  }

  .viewer-mapbox .mapboxgl-canvas {
    left: 0;
  }
</style>
